import { useState } from 'react';
import { Lock, RefreshCw, LogOut } from 'lucide-react';
import { authApi } from '@/api/endpoints/auth';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';

const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

// Troca de senha obrigatória no 1º acesso (senha temporária) — também acessível avulso.
export function ChangePassword() {
  const { logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const valid = current.length >= 1 && next.length >= 8 && next === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 8) return setError('A nova senha deve ter no mínimo 8 caracteres.');
    if (next !== confirm) return setError('A confirmação não coincide com a nova senha.');
    setSaving(true);
    setError(null);
    try {
      await authApi.changePassword({ current_password: current, new_password: next });
      window.location.href = '/';
    } catch (err) {
      const fallback = 'Não foi possível trocar a senha. Verifique a senha atual.';
      let msg = fallback;
      if (typeof err === 'object' && err !== null) {
        const maybe = err as { response?: { data?: { message?: string } } };
        msg = maybe.response?.data?.message ?? fallback;
      }
      setError(msg);
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg-main p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-card">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Lock size={20} />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-text-title">Trocar senha</h1>
            <p className="text-xs text-text-secondary">Defina uma nova senha para continuar.</p>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-title">
              Senha atual (temporária)
            </label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputClass}
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-title">Nova senha</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputClass}
              placeholder="Mínimo 8 caracteres"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-title">
              Confirmar nova senha
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
          </div>
          <Button type="submit" className="w-full" disabled={!valid || saving}>
            {saving && <RefreshCw size={16} className="animate-spin" />}
            {saving ? 'Salvando...' : 'Salvar nova senha'}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => logout()}
          className="mt-4 flex w-full items-center justify-center gap-1.5 text-xs font-medium text-text-secondary transition-colors hover:text-text-title"
        >
          <LogOut size={13} /> Sair
        </button>
      </div>
    </div>
  );
}
